// What cloud keeps doing for notices while the person is signed in, drawing
// nothing (mounted by Me as a part's Background):
// - this browser: when it chose to receive and its subscription vanished while
//   the permission stayed, subscribe again silently; when it receives under
//   an endpoint it does not remember yet and the server knows that endpoint,
//   remember it (a browser turned off elsewhere is not turned on again);
// - inside the desktop app: register this installation once per start unless
//   the person turned it off here (receiving is the default there), and tell
//   the app whether delivery to it is on (it then checks the mac permission);
//   inside the desktop app's screen (a frame there) the same, once the app
//   said who it is;
// - inside the iOS app: register its APNs token once the person allowed its
//   notifications, unless the person turned it off here (receiving is the
//   default there too).
// Failures stay quiet: the settings show the switch off, to try again there.
import { useQuery } from "@tanstack/react-query";
import {
  type AppNoticeHost,
  appNoticeHost,
  defineScreenPart,
  noticeDesktop,
  onAppNotices,
  useApi,
  useNoticeDesktop,
} from "@trameria/ui-core";
import { useEffect, useMemo, useRef } from "react";
import { accountQuery } from "../queries";
import {
  appTurnedOff,
  desktopTurnedOff,
  mayRestore,
  rememberEndpoint,
  rememberedEndpoint,
} from "./push";
import {
  appNoticesQuery,
  browserPushQuery,
  pushQuery,
  useApplyAppNotices,
  useRegisterApp,
  useRegisterDesktop,
  useRestoreSubscription,
} from "./queries";

function useRestoreBrowserPush(enabled: boolean) {
  const api = useApi();
  const browser = useQuery({ ...browserPushQuery(), enabled });
  const lost = enabled && browser.data?.kind === "unsubscribed" && mayRestore();
  const live =
    browser.data?.kind === "subscribed" ? browser.data.endpoint : null;
  const unremembered = live !== null && live !== rememberedEndpoint();
  const push = useQuery({
    ...pushQuery(api),
    enabled: enabled && (lost || unremembered),
  });
  const publicKey = push.data?.publicKey ?? null;
  const serverKnowsLive =
    live !== null &&
    (push.data?.subscriptions.some((s) => s.endpoint === live) ?? false);
  const { mutate } = useRestoreSubscription();
  const started = useRef(false);
  // frontend-development: write-only (subscribing again, remembering on this device); the reads are the queries above. Once per start.
  useEffect(() => {
    if (unremembered && serverKnowsLive && live) rememberEndpoint(live);
    if (lost && publicKey && !started.current) {
      started.current = true;
      mutate(publicKey);
    }
  }, [lost, publicKey, unremembered, serverKnowsLive, live, mutate]);
}

function useDesktopDestination(installationId: string | null) {
  const api = useApi();
  const wanted = installationId !== null && !desktopTurnedOff();
  const push = useQuery({
    ...pushQuery(api),
    enabled: installationId !== null,
  });
  const register = useRegisterDesktop();
  const started = useRef(false);
  const { mutate } = register;
  // frontend-development: write-only (registering this desktop as a destination), once per start.
  useEffect(() => {
    if (!wanted || !installationId || started.current) return;
    started.current = true;
    mutate(installationId);
  }, [wanted, installationId, mutate]);

  const listed =
    push.data?.subscriptions.some(
      (s) => s.kind === "desktop" && s.id === installationId,
    ) ?? false;
  // Not listed yet while registering is not "off".
  const registering = wanted && (register.isIdle || register.isPending);
  const target =
    installationId === null || !push.isSuccess
      ? null
      : listed
        ? true
        : registering
          ? null
          : false;
  // frontend-development: write-only (telling the app whether delivery to it is on).
  useEffect(() => {
    if (target !== null) noticeDesktop()?.setNotificationTarget?.(target);
  }, [target]);
}

/**
 * Inside the iOS app: once the person allowed its notifications (the app asks
 * when the first screen shows), register its token, once per start and again
 * when the app has a new one, unless the person turned it off here.
 */
function useAppDestination(host: AppNoticeHost | null) {
  const state = useQuery({
    ...appNoticesQuery(host as AppNoticeHost),
    enabled: host !== null,
  });
  const apply = useApplyAppNotices();
  // frontend-development: not the server's stream; hears what the app says on its own (the person allowed, or came back from its settings).
  useEffect(
    () => (host ? onAppNotices(host, apply) : undefined),
    [host, apply],
  );
  const { mutate } = useRegisterApp(host as AppNoticeHost);
  const registered = useRef<string | null>(null);
  const token =
    state.data?.status === "authorized" ? (state.data.token ?? null) : null;
  // frontend-development: write-only (registering this app as a destination), once per token.
  useEffect(() => {
    if (!state.data || !token || appTurnedOff()) return;
    if (registered.current === token) return;
    registered.current = token;
    mutate(state.data);
  }, [state.data, token, mutate]);
}

function SignedInBackground() {
  // Inside the desktop app's frame, known once the app said who it is.
  const installationId = useNoticeDesktop()?.installationId ?? null;
  const app = useMemo(() => appNoticeHost(), []);
  useRestoreBrowserPush(installationId === null && app === null);
  useDesktopDestination(installationId);
  useAppDestination(app);
  return null;
}

function CloudNoticesBackground() {
  const api = useApi();
  const account = useQuery(accountQuery(api));
  // Mounted on every page (sign-in too); only a signed-in person has destinations.
  return account.data?.approved ? <SignedInBackground /> : null;
}

export const cloudNoticesPart = defineScreenPart({
  id: "cloud-notices",
  Background: CloudNoticesBackground,
});
